import { ApiError } from "@groundspark/browser-auth";

export type SaveState = "saved" | "waiting" | "saving" | "error";

/**
 * Serialize saves so edits made during a request always use its returned revision.
 * Content is read when a save starts, so edits are counted rather than copied.
 */
export class Autosave<Content, Saved extends { readonly revision: number }> {
  private revision: number;
  private changes = 0;
  private savedChanges = 0;
  private unconfirmed: { readonly changes: number; readonly content: Content } | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private pending: Promise<boolean> | null = null;
  private disposed = false;

  public constructor(
    revision: number,
    private readonly read: () => Content,
    private readonly persist: (revision: number, content: Content) => Promise<Saved>,
    private readonly onSaved: (saved: Saved) => void,
    private readonly onState: (state: SaveState, error?: unknown) => void,
    private readonly delay = 500,
  ) {
    this.revision = revision;
  }

  public get dirty(): boolean {
    return this.savedChanges < this.changes;
  }

  public get savedRevision(): number {
    return this.revision;
  }

  public change(): void {
    if (this.disposed) return;
    this.changes += 1;
    this.onState("waiting");
    this.clearTimer();
    this.timer = setTimeout(() => void this.flush(), this.delay);
  }

  public flush(): Promise<boolean> {
    this.clearTimer();
    if (this.pending !== null) return this.pending;
    if (this.disposed) return Promise.resolve(false);
    this.pending = this.save().finally(() => {
      this.pending = null;
    });
    return this.pending;
  }

  public destroy(): void {
    this.disposed = true;
    this.clearTimer();
  }

  private async save(): Promise<boolean> {
    while (this.dirty && !this.disposed) {
      let attempt = this.unconfirmed;
      this.onState("saving");
      try {
        attempt ??= { changes: this.changes, content: this.read() };
        const saved = await this.persist(this.revision, attempt.content);
        if (this.disposed) return false;
        this.revision = saved.revision;
        this.unconfirmed = null;
        this.savedChanges = Math.max(this.savedChanges, attempt.changes);
        this.onSaved(saved);
      } catch (error) {
        // A transport failure may follow a committed save. Replay that exact request
        // before sending newer edits, allowing the server to acknowledge it safely.
        this.unconfirmed =
          attempt === null ||
          (error instanceof ApiError &&
            error.status !== null &&
            error.status >= 400 &&
            error.status < 500)
            ? null
            : attempt;
        this.clearTimer();
        if (!this.disposed) this.onState("error", error);
        return false;
      }
    }
    if (this.disposed) return false;
    this.onState("saved");
    return true;
  }

  private clearTimer(): void {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
  }
}
